'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, ImageField, LinkField, isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';

export type FeatureOverviewHighlightProps =
  SliceComponentProps<Content.FeatureOverviewHighlightSlice>;

const FeatureOverviewHighlight: FC<FeatureOverviewHighlightProps> = ({
  slice,
}) => {
  const { eyebrow, title, description, cta, features } = slice.primary;
  const ctaGreen = (slice.primary as { cta_green?: LinkField }).cta_green;

  const cardRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const cards = cardRefs.current.filter(
      (el): el is HTMLElement => el !== null,
    );
    if (cards.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0', 'translate-y-6');
            entry.target.classList.add('opacity-100', 'translate-y-0');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, [features?.length]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white font-inter py-20 px-6 md:px-10"
    >
      <div className="mx-auto w-full max-w-[1200px] flex flex-col gap-[60px]">
        {eyebrow && (
          <div className="flex items-center gap-8 md:gap-[60px] w-full">
            <span className="inline-flex items-center justify-center border border-[#4B0FEA] rounded-[89px] px-4 py-2 text-[#041118] text-[14px] leading-[1.2] uppercase whitespace-nowrap">
              {eyebrow}
            </span>
            <span className="flex-1 h-px bg-[#CCCCCC]" aria-hidden="true" />
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-12 md:gap-20 items-start w-full">
          <div className="flex-1 flex flex-col gap-6 min-w-0 md:sticky md:top-24 md:self-start">
            {isFilled.richText(title) && (
              <div className="text-[#041118] text-[32px] md:text-[42px] font-semibold leading-[1.4] capitalize [&_h2]:m-0">
                <PrismicRichText field={title} />
              </div>
            )}

            {isFilled.richText(description) && (
              <div className="text-[#041118] text-[16px] font-light leading-[1.5] [&_p]:m-0 [&_p+p]:mt-4">
                <PrismicRichText field={description} />
              </div>
            )}

            {(isFilled.link(cta) || (ctaGreen && isFilled.link(ctaGreen))) && (
              <div className="flex flex-wrap items-center gap-3">
                {isFilled.link(cta) && (
                  <PrismicNextLink
                    field={cta}
                    className="inline-flex items-center justify-center border border-[#041118] rounded-[100px] px-6 py-3 font-manrope font-bold text-[16px] leading-[1.5] text-[#041118] whitespace-nowrap transition-colors hover:bg-[#041118] hover:text-white"
                  >
                    {cta.text || 'Learn more'}
                  </PrismicNextLink>
                )}
                {ctaGreen && isFilled.link(ctaGreen) && (
                  <PrismicNextLink
                    field={ctaGreen}
                    className="inline-flex items-center justify-center rounded-[100px] bg-[#00f557] px-6 py-3 font-manrope text-[16px] font-bold leading-[1.5] text-[#002726] whitespace-nowrap transition-opacity hover:opacity-90"
                  >
                    {ctaGreen.text || 'Book a Discovery Session'}
                  </PrismicNextLink>
                )}
              </div>
            )}
          </div>

          {features && features.length > 0 && (
            <div className="w-full md:w-[460px] md:shrink-0 flex flex-col gap-6">
              {features.map((item, i) => {
                const logo = (item as { feature_logo?: ImageField })
                  .feature_logo;
                const heading = (item as { feature_heading?: string })
                  .feature_heading;
                const authorName = (item as { author_name?: string })
                  .author_name;
                const authorRole = (item as { author_role?: string })
                  .author_role;

                return (
                  <article
                    key={i}
                    ref={(el) => {
                      cardRefs.current[i] = el;
                    }}
                    style={{ transitionDelay: `${i * 120}ms` }}
                    className="opacity-0 translate-y-6 will-change-transform transition-[opacity,transform,box-shadow,border-color] duration-700 ease-out motion-reduce:opacity-100 motion-reduce:translate-y-0 motion-reduce:transition-none border border-[#CCCCCC] rounded-[24px] p-9 flex flex-col gap-6 backdrop-blur-[21px] hover:-translate-y-1 hover:border-[#4B0FEA] hover:shadow-[0_12px_32px_-16px_rgba(75,15,234,0.25)]"
                  >
                    {logo && isFilled.image(logo) ? (
                      <header className="flex items-center">
                        <PrismicNextImage
                          field={logo}
                          className="h-[28px] w-auto max-w-[160px] object-contain object-left"
                          fallbackAlt=""
                        />
                      </header>
                    ) : (
                      item.feature_label && (
                        <header className="flex items-center gap-4">
                          <span
                            className="shrink-0 size-3 rounded-full border border-[#4B0FEA]"
                            aria-hidden="true"
                          />
                          <span className="text-[#4B0FEA] text-[14px] leading-[1.2] uppercase">
                            {item.feature_label}
                          </span>
                        </header>
                      )
                    )}

                    <div className="flex flex-col gap-3">
                      {isFilled.richText(item.feature_title) && (
                        <div className="font-inter text-[#4B0FEA] [&_h4]:m-0 [&_h4]:text-[24px] [&_h4]:font-semibold [&_h4]:leading-[1.2]">
                          <PrismicRichText field={item.feature_title} />
                        </div>
                      )}
                      {heading && (
                        <h4 className="text-[#041118] text-[20px] md:text-[22px] font-bold leading-[1.3]">
                          {heading}
                        </h4>
                      )}
                      {isFilled.richText(item.feature_description) && (
                        <div className="font-inter text-[#041118] [&_p]:m-0 [&_p]:text-[16px] [&_p]:font-light [&_p]:leading-[1.5] [&_p+p]:mt-2">
                          <PrismicRichText field={item.feature_description} />
                        </div>
                      )}
                    </div>

                    {(authorName || authorRole) && (
                      <footer className="flex flex-col gap-0.5 text-[#041118]">
                        {authorName && (
                          <p className="text-[14px] font-bold leading-[1.4]">
                            {authorName}
                          </p>
                        )}
                        {authorRole && (
                          <p className="text-[14px] font-bold leading-[1.4]">
                            {authorRole}
                          </p>
                        )}
                      </footer>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureOverviewHighlight;
